import { type VariantProps, cva } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@ui/lib/utils';

const badgeVariants = cva(
  'inline-flex items-center rounded-md border px-2.5 py-0.5 text-xs font-semibold font-manrope transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
  {
    variants: {
      variant: {
        default: 'border-transparent bg-primary-500 text-primary-foreground shadow hover:bg-primary-500/80',
        secondary: 'border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80',
        destructive: 'border-transparent bg-destructive text-destructive-foreground shadow hover:bg-destructive/80',
        outline: 'text-foreground',
        // Soft status chips — low-contrast tinted backgrounds for inline state
        // labels (capacity, cancellation, counts). Match PaymentStatusBadge.
        successSoft: 'border-green-200 bg-green-50 text-green-700',
        warningSoft: 'border-amber-200 bg-amber-50 text-amber-700',
        errorSoft: 'border-red-200 bg-red-50 text-red-600',
        neutralSoft: 'border-gray-200 bg-gray-100 text-gray-700'
      }
    },
    defaultVariants: {
      variant: 'default'
    }
  }
);

export interface BadgeProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof badgeVariants> {
  icon?: React.ReactNode;
}

function Badge({ className, variant, icon, children, ...props }: BadgeProps) {
  return (
    <div className={cn(badgeVariants({ variant }), className)} {...props}>
      {icon && <span className="mr-1.5">{icon}</span>}
      {children}
    </div>
  );
}

// eslint-disable-next-line react-refresh/only-export-components -- variant export pattern from shadcn-ui
export { Badge, badgeVariants };
